import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Trash2, Star, Quote, UserX, Copy, MessageSquareQuote, Film, Upload, Eye, RefreshCw, Image as ImageIcon, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api, { getErrorMessage } from "@/lib/api";

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// Reusable drag & drop upload zone.
const Dropzone = ({ accept, hint, onFile, progress, icon: Icon, label, testid }) => {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const uploading = progress !== null && progress !== undefined;
  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => !uploading && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-secondary/40"}`}
      data-testid={testid}
    >
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
      {uploading ? (
        <div className="w-full max-w-xs">
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-primary"><Loader2 className="h-4 w-4 animate-spin" /> Uploading… {progress}%</p>
          <Progress value={progress} className="mt-3" data-testid={`${testid}-progress`} />
        </div>
      ) : (
        <>
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
          <p className="mt-3 text-sm font-medium text-foreground">{label}</p>
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        </>
      )}
    </div>
  );
};

export default function AdminContent() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ quote: "", author: "Airbnb Host", location: "", rating: 5 });
  const [walkthrough, setWalkthrough] = useState(null);
  const [videoProgress, setVideoProgress] = useState(null); // null = idle, 0-100 = uploading
  const [thumbProgress, setThumbProgress] = useState(null);
  const [confirm, setConfirm] = useState(null); // "video" | "thumbnail" | null
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && user && !user.is_admin) navigate("/app/dashboard", { replace: true });
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    try {
      const [t, a, w] = await Promise.all([
        api.get("/admin/testimonials"),
        api.get("/admin/abandoned-signups"),
        api.get("/site/walkthrough"),
      ]);
      setTestimonials(t.data.testimonials || []);
      setDefaults(t.data.defaults || []);
      setAbandoned(a.data.abandoned || []);
      setWalkthrough(w.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load content."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const addTestimonial = async (e) => {
    e.preventDefault();
    if (form.quote.trim().length < 4) { toast.error("Please enter a testimonial quote."); return; }
    setSaving(true);
    try {
      await api.post("/admin/testimonials", form);
      toast.success("Testimonial published.");
      setForm({ quote: "", author: "Airbnb Host", location: "", rating: 5 });
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not save testimonial."));
    } finally {
      setSaving(false);
    }
  };

  const removeTestimonial = async (id) => {
    setTestimonials((prev) => prev.filter((t) => t.id !== id));
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Testimonial removed.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not remove."));
      load();
    }
  };

  const copyRecovery = (a) => {
    const url = `${window.location.origin}/signup?email=${encodeURIComponent(a.email)}&rt=${a.recovery_token}`;
    navigator.clipboard?.writeText(url);
    toast.success("Recovery link copied.");
  };

  const VIDEO_MAX = 150 * 1024 * 1024;
  const THUMB_MAX = 10 * 1024 * 1024;
  const VIDEO_EXT = ["mp4", "mov", "webm", "m4v"];
  const THUMB_EXT = ["jpg", "jpeg", "png", "webp"];
  const fmtSize = (b) => b == null ? "—" : (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const mediaUrl = (u) => (u ? (u.startsWith("/") ? `${BACKEND}${u}` : u) : null);

  const uploadVideo = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!file.type.startsWith("video") && !VIDEO_EXT.includes(ext)) {
      toast.error("Unsupported file. Please upload an MP4, MOV or WebM video."); return;
    }
    if (file.size > VIDEO_MAX) { toast.error(`Video is ${(file.size / 1048576).toFixed(0)}MB — the maximum is 150MB.`); return; }
    const fd = new FormData();
    fd.append("file", file);
    setVideoProgress(0);
    try {
      const r = await api.post("/admin/walkthrough/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setVideoProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0),
      });
      setWalkthrough(r.data);
      toast.success("Walkthrough video updated successfully.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Upload failed. Please try again."));
    } finally { setVideoProgress(null); }
  };

  const uploadThumbnail = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) && !THUMB_EXT.includes(ext)) {
      toast.error("Unsupported image. Please upload a JPG, PNG or WEBP file."); return;
    }
    if (file.size > THUMB_MAX) { toast.error(`Image is ${(file.size / 1048576).toFixed(1)}MB — the maximum is 10MB.`); return; }
    const fd = new FormData();
    fd.append("file", file);
    setThumbProgress(0);
    try {
      const r = await api.post("/admin/walkthrough/thumbnail", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setThumbProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0),
      });
      setWalkthrough(r.data);
      toast.success("Thumbnail updated successfully.");
    } catch (e) {
      toast.error(getErrorMessage(e, "Thumbnail upload failed."));
    } finally { setThumbProgress(null); }
  };

  const removeVideo = async () => {
    setConfirm(null);
    try {
      const r = await api.delete("/admin/walkthrough");
      setWalkthrough(r.data);
      toast.success("Walkthrough video removed.");
    } catch (e) { toast.error(getErrorMessage(e, "Could not remove video.")); }
  };

  const removeThumbnail = async () => {
    setConfirm(null);
    try {
      const r = await api.delete("/admin/walkthrough/thumbnail");
      setWalkthrough(r.data);
      toast.success("Thumbnail removed.");
    } catch (e) { toast.error(getErrorMessage(e, "Could not remove thumbnail.")); }
  };

  if (authLoading || (user && !user.is_admin)) {
    return <AppShell title="Content"><div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppShell>;
  }

  const activeList = testimonials.length ? testimonials : defaults;

  return (
    <AppShell title="Content & Leads">
      <div className="space-y-6" data-testid="admin-content-page">
        <Tabs defaultValue="testimonials">
          <TabsList data-testid="content-tabs">
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">
              <MessageSquareQuote className="mr-1.5 h-4 w-4" /> Testimonials
            </TabsTrigger>
            <TabsTrigger value="abandoned" data-testid="tab-abandoned">
              <UserX className="mr-1.5 h-4 w-4" /> Abandoned signups {abandoned.length > 0 && <Badge className="ml-1.5 bg-primary/10 text-primary">{abandoned.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="walkthrough" data-testid="tab-walkthrough">
              <Film className="mr-1.5 h-4 w-4" /> Walkthrough video
            </TabsTrigger>
          </TabsList>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="mt-5 space-y-6">
            <form onSubmit={addTestimonial} className="rounded-xl border bg-card p-5" data-testid="testimonial-form">
              <h3 className="font-display text-base font-semibold">Add a testimonial</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">Published testimonials replace the default ones on the landing page.</p>
              <div className="mt-4 grid gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="quote">Quote</Label>
                  <Textarea id="quote" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} placeholder="What did the host say?" rows={3} data-testid="testimonial-quote-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="author">Author</Label>
                    <Input id="author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="testimonial-author-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="location">Location <span className="text-muted-foreground">(optional)</span></Label>
                    <Input id="location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Gold Coast, QLD" data-testid="testimonial-location-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rating">Rating</Label>
                    <div className="flex items-center gap-1 pt-1.5" data-testid="testimonial-rating-input">
                      {[1, 2, 3, 4, 5].map((r) => (
                        <button key={r} type="button" onClick={() => setForm({ ...form, rating: r })} aria-label={`${r} stars`}>
                          <Star className={`h-5 w-5 ${r <= form.rating ? "fill-[hsl(var(--risk-medium))] text-[hsl(var(--risk-medium))]" : "text-muted-foreground/40"}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <Button type="submit" disabled={saving} className="gap-1.5" data-testid="testimonial-submit">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Publish testimonial
                  </Button>
                </div>
              </div>
            </form>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <h3 className="font-display text-base font-semibold">Live testimonials</h3>
                {!testimonials.length && <Badge variant="secondary">Showing defaults</Badge>}
              </div>
              {loading ? (
                <div className="grid gap-3 sm:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="testimonial-list">
                  {activeList.map((t) => (
                    <div key={t.id} className="relative rounded-xl border bg-card p-5" data-testid="testimonial-card">
                      <Quote className="h-5 w-5 text-primary/30" />
                      <div className="mt-2 flex gap-0.5">
                        {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[hsl(var(--risk-medium))] text-[hsl(var(--risk-medium))]" />)}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-foreground">&ldquo;{t.quote}&rdquo;</p>
                      <p className="mt-3 text-xs font-medium text-muted-foreground">— {t.author}{t.location ? `, ${t.location}` : ""}</p>
                      {testimonials.length > 0 && (
                        <button onClick={() => removeTestimonial(t.id)} className="absolute right-3 top-3 text-muted-foreground hover:text-[hsl(var(--risk-high))]" aria-label="Delete testimonial" data-testid={`testimonial-delete-${t.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ABANDONED SIGNUPS */}
          <TabsContent value="abandoned" className="mt-5">
            <div className="rounded-xl border bg-card p-5">
              <h3 className="font-display text-base font-semibold">Abandoned signups</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">Visitors who entered an email but didn&apos;t finish registering. Copy a recovery link to reach out manually.</p>
              {loading ? (
                <div className="mt-4 space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
              ) : abandoned.length === 0 ? (
                <div className="mt-6 flex flex-col items-center justify-center py-10 text-center" data-testid="abandoned-empty">
                  <UserX className="h-9 w-9 text-muted-foreground/40" />
                  <p className="mt-2 text-sm font-medium">No abandoned signups</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Incomplete registrations will appear here.</p>
                </div>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="abandoned-table">
                    <thead>
                      <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">Email</th>
                        <th className="pb-2 pr-4 font-medium">Started</th>
                        <th className="pb-2 pr-4 font-medium">Device</th>
                        <th className="pb-2 pr-4 font-medium">IP</th>
                        <th className="pb-2 font-medium">Recovery</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {abandoned.map((a) => (
                        <tr key={a.id} data-testid="abandoned-row">
                          <td className="py-2.5 pr-4 font-medium text-foreground">{a.email}</td>
                          <td className="py-2.5 pr-4 text-muted-foreground">{fmtDateTime(a.started_at)}</td>
                          <td className="py-2.5 pr-4 text-muted-foreground capitalize">{a.device || "—"}</td>
                          <td className="py-2.5 pr-4 text-muted-foreground">{a.ip || "—"}</td>
                          <td className="py-2.5">
                            <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-muted-foreground" onClick={() => copyRecovery(a)} data-testid={`abandoned-copy-${a.id}`}>
                              <Copy className="h-3.5 w-3.5" /> Copy link
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* WALKTHROUGH VIDEO */}
          <TabsContent value="walkthrough" className="mt-5 space-y-6" data-testid="walkthrough-admin">
            <div>
              <h3 className="font-display text-base font-semibold">Homepage walkthrough video</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">Manage the &ldquo;How HostGuard works in 60 seconds&rdquo; video. Changes appear on the homepage instantly. Uploading a new file automatically replaces the old one.</p>
            </div>

            {/* Current media preview */}
            <div className="grid gap-5 lg:grid-cols-2">
              {/* VIDEO */}
              <div className="rounded-xl border bg-card p-5" data-testid="current-video-card">
                <div className="flex items-center gap-2">
                  <Film className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Current walkthrough video</h4>
                </div>
                {walkthrough?.configured ? (
                  <>
                    <div className="mt-3 overflow-hidden rounded-lg border bg-muted">
                      <div className="aspect-video w-full">
                        {walkthrough.thumbnail_url ? (
                          <img src={mediaUrl(walkthrough.thumbnail_url)} alt="Video thumbnail" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-[hsl(222,55%,26%)] text-primary-foreground"><Film className="h-8 w-8 opacity-80" /></div>
                        )}
                      </div>
                    </div>
                    <dl className="mt-3 space-y-1 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Filename</dt><dd className="truncate font-medium" title={walkthrough.video_filename}>{walkthrough.video_filename || (walkthrough.video_type === "url" ? "External URL" : "—")}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-muted-foreground">File size</dt><dd className="font-medium">{fmtSize(walkthrough.video_size)}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Uploaded</dt><dd className="font-medium">{fmtDateTime(walkthrough.video_uploaded_at)}</dd></div>
                    </dl>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPreviewOpen(true)} data-testid="video-preview-btn"><Eye className="h-3.5 w-3.5" /> Preview</Button>
                      <Button size="sm" variant="destructive" className="gap-1.5" onClick={() => setConfirm("video")} data-testid="video-remove-btn"><Trash2 className="h-3.5 w-3.5" /> Remove</Button>
                    </div>
                  </>
                ) : (
                  <p className="mt-3 rounded-lg border border-dashed bg-secondary/40 px-3 py-6 text-center text-sm text-muted-foreground">No walkthrough video yet. Upload one below.</p>
                )}
                <div className="mt-4">
                  <Dropzone
                    accept="video/mp4,video/quicktime,video/webm"
                    icon={walkthrough?.configured ? RefreshCw : Upload}
                    label={walkthrough?.configured ? "Replace video" : "Upload video"}
                    hint="Drag & drop or click · MP4, MOV, WebM · up to 150MB"
                    onFile={uploadVideo}
                    progress={videoProgress}
                    testid="video-dropzone"
                  />
                </div>
              </div>

              {/* THUMBNAIL */}
              <div className="rounded-xl border bg-card p-5" data-testid="current-thumbnail-card">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-primary" />
                  <h4 className="font-medium">Current thumbnail</h4>
                </div>
                {walkthrough?.thumbnail_url ? (
                  <>
                    <div className="mt-3 overflow-hidden rounded-lg border bg-muted">
                      <div className="aspect-video w-full">
                        <img src={mediaUrl(walkthrough.thumbnail_url)} alt="Current thumbnail" className="h-full w-full object-cover" data-testid="thumbnail-preview-img" />
                      </div>
                    </div>
                    <dl className="mt-3 space-y-1 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Filename</dt><dd className="truncate font-medium" title={walkthrough.thumbnail_filename}>{walkthrough.thumbnail_filename || "—"}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-muted-foreground">File size</dt><dd className="font-medium">{fmtSize(walkthrough.thumbnail_size)}</dd></div>
                    </dl>
                    <div className="mt-4">
                      <Button size="sm" variant="destructive" className="gap-1.5" onClick={() => setConfirm("thumbnail")} data-testid="thumbnail-remove-btn"><Trash2 className="h-3.5 w-3.5" /> Remove</Button>
                    </div>
                  </>
                ) : (
                  <p className="mt-3 rounded-lg border border-dashed bg-secondary/40 px-3 py-6 text-center text-sm text-muted-foreground">No thumbnail set. The homepage will use the video&apos;s first frame.</p>
                )}
                <div className="mt-4">
                  <Dropzone
                    accept="image/jpeg,image/png,image/webp"
                    icon={walkthrough?.thumbnail_url ? RefreshCw : Upload}
                    label={walkthrough?.thumbnail_url ? "Replace thumbnail" : "Upload thumbnail"}
                    hint="Drag & drop or click · JPG, PNG, WEBP · up to 10MB"
                    onFile={uploadThumbnail}
                    progress={thumbProgress}
                    testid="thumbnail-dropzone"
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Video preview modal */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl overflow-hidden border-0 bg-black p-0" data-testid="video-preview-modal">
            <div className="aspect-video w-full bg-black">
              {previewOpen && walkthrough?.configured && (
                <video src={mediaUrl(walkthrough.video_url)} poster={mediaUrl(walkthrough.thumbnail_url) || undefined} controls autoPlay className="h-full w-full bg-black" />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Remove confirmation */}
        <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
          <AlertDialogContent data-testid="walkthrough-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {confirm === "video" ? "walkthrough video" : "thumbnail"}?</AlertDialogTitle>
              <AlertDialogDescription>
                {confirm === "video"
                  ? "This removes the video from the homepage immediately. The walkthrough section will be hidden until you upload a new video."
                  : "This removes the thumbnail. The homepage will fall back to the video's first frame."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirm === "video" ? removeVideo : removeThumbnail} data-testid="confirm-remove">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}
